import { useMemo } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { InvitationPreview } from '../../InvitationPreview';
import { FOCUS_RING } from '../constants';
import { PanelHeading } from '../controls';
import { TEXT_DESIGNS, applyTextDesign, originalTextDesign } from '../textDesigns';

/** Show only the background's text space (template.text_area, % of the page) so the lettering reads at tile size. */
const cropOf = (area) => {
  if (!area || !(area.x1 > area.x0) || !(area.y1 > area.y0)) return null;
  const pad = 3;
  const x0 = Math.max(0, area.x0 - pad);
  const x1 = Math.min(100, area.x1 + pad);
  const y0 = Math.max(0, area.y0 - pad);
  const y1 = Math.min(100, area.y1 + pad);
  const w = x1 - x0;
  const h = y1 - y0;
  return {
    outer: { aspectRatio: `${w * 8.5} / ${h * 11}` },
    inner: { aspectRatio: '8.5 / 11', isolation: 'isolate', position: 'absolute', width: `${(100 * 100) / w}%`, left: `${(-x0 * 100) / w}%`, top: `${(-y0 * 100) / h}%` },
  };
};

/**
 * Ready text tool: five ready-made text designs for the current background (fonts, sizes, colours),
 * each previewed on the background with the customer's own wording, plus the template's original.
 */
export const TextDesignsPanel = ({ design, template, fonts, designs = TEXT_DESIGNS, backgroundImage, activeId, onApply }) => {
  const crop = cropOf(template?.text_area);
  const families = useMemo(() => new Set(fonts.map((f) => f.family)), [fonts]);
  const options = useMemo(() => {
    const ctx = { fields: design.text_fields, template, families };
    return [
      { id: 'original', name: 'Original', fields: originalTextDesign(ctx) },
      ...designs.map((d) => ({ id: d.id, name: d.name, fields: applyTextDesign(d, ctx) })),
    ];
  }, [design.text_fields, template, families, designs]);

  return (
    <div className="flex flex-col gap-3.5" data-testid="text-designs-panel">
      <PanelHeading>Ready text</PanelHeading>
      <p className="text-[13px] leading-relaxed text-[#4A4640]">
        Ready-made text designs for this background: fonts, sizes and colours change, your wording stays.
      </p>
      <div className="grid grid-cols-2 gap-x-2.5 gap-y-3">
        {options.map((o) => {
          const on = activeId === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onApply(o)}
              aria-pressed={on}
              aria-label={`Use the ${o.name} text design`}
              className={cn('group flex flex-col gap-1.5 text-left rounded-lg', FOCUS_RING)}
              data-testid={`text-design-${o.id}`}
            >
              <span
                className={cn(
                  'relative block w-full rounded-md overflow-hidden',
                  on ? 'ring-2 ring-ink ring-offset-2 ring-offset-white' : 'border border-[#E6E1D6] group-hover:border-[#B8B2A5]'
                )}
              >
                <span className="relative block overflow-hidden" style={crop?.outer}>
                  <InvitationPreview
                    backgroundImage={backgroundImage}
                    fields={o.fields}
                    elements={design.elements}
                    background={design.background}
                    alt=""
                    style={crop?.inner}
                  />
                </span>
                {on && (
                  <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-ink text-paper flex items-center justify-center">
                    <Check className="h-3 w-3" aria-hidden="true" />
                  </span>
                )}
              </span>
              <span className="text-[11.5px] font-bold text-ink leading-tight">{o.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default TextDesignsPanel;
